import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Loader2, Mail, Paperclip } from 'lucide-react';
import { projectsApi } from '../../lib/api';
import { toast } from 'sonner';

export const EmailWorkbookDialog = ({ open, onOpenChange, projectId }) => {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [recipients, setRecipients] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [filename, setFilename] = useState('');
  const debounceRef = useRef(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!open) {
      initializedRef.current = false;
      return;
    }
    setLoading(true);
    projectsApi.workbookEmailPreview(projectId, {})
      .then(({ data }) => {
        setRecipients((data.default_recipients || []).join(', '));
        setSubject(data.subject || '');
        setMessage(data.message || '');
        setPreviewHtml(data.html || '');
        setFilename(data.filename || '');
        initializedRef.current = true;
      })
      .catch(() => toast.error('Failed to load email preview'))
      .finally(() => setLoading(false));
  }, [open, projectId]);

  useEffect(() => {
    if (!open || !initializedRef.current) return undefined;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      projectsApi.workbookEmailPreview(projectId, { subject, message })
        .then(({ data }) => setPreviewHtml(data.html || ''))
        .catch(() => {});
    }, 600);
    return () => clearTimeout(debounceRef.current);
  }, [subject, message, open, projectId]);

  const handleSend = async () => {
    const list = recipients.split(/[,;\s]+/).map((e) => e.trim()).filter(Boolean);
    if (list.length === 0) {
      toast.error('Add at least one recipient');
      return;
    }
    setSending(true);
    try {
      const { data } = await projectsApi.sendWorkbookEmail(projectId, {
        recipients: list,
        subject,
        message,
      });
      if (data.failed?.length) {
        toast.warning(`Sent to ${data.sent.join(', ')} — failed for ${data.failed.join(', ')}`);
      } else {
        toast.success(`Workbook emailed to ${data.sent.join(', ')}`);
      }
      onOpenChange(false);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="email-workbook-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-emerald-700" /> Email Workbook Reconciliation
          </DialogTitle>
          <DialogDescription>
            Review the email exactly as it will be received. Nothing is sent until you click Send.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Preparing preview…
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <Label htmlFor="wb-email-recipients">To (comma-separated)</Label>
                <Input
                  id="wb-email-recipients"
                  value={recipients}
                  onChange={(e) => setRecipients(e.target.value)}
                  placeholder="investor@example.com, cpa@example.com"
                  data-testid="email-recipients-input"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Pre-filled with this deal's JV investor(s). Add anyone else (CPA, attorney…).
                </p>
              </div>
              <div>
                <Label htmlFor="wb-email-subject">Subject</Label>
                <Input
                  id="wb-email-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  data-testid="email-subject-input"
                />
              </div>
              <div>
                <Label htmlFor="wb-email-message">Message</Label>
                <Textarea
                  id="wb-email-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={9}
                  data-testid="email-message-input"
                />
              </div>
              <div className="flex items-center gap-2 p-2.5 rounded-sm border bg-muted/40 text-xs" data-testid="email-attachment-chip">
                <Paperclip className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
                <span className="font-mono truncate">{filename}</span>
                <span className="text-muted-foreground shrink-0">— generated fresh at send time</span>
              </div>
            </div>

            <div className="flex flex-col">
              <Label className="mb-1.5">Live Preview</Label>
              <div className="border rounded-sm bg-gray-100 p-2 flex-1 min-h-[380px]">
                <iframe
                  title="Email preview"
                  srcDoc={previewHtml}
                  sandbox=""
                  className="w-full h-full min-h-[370px] bg-white rounded-sm border-0"
                  data-testid="email-preview-frame"
                />
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} disabled={sending} data-testid="email-cancel-btn">
            Cancel
          </Button>
          <Button
            className="rounded-sm bg-emerald-700 hover:bg-emerald-800"
            onClick={handleSend}
            disabled={loading || sending}
            data-testid="email-send-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
            {sending ? 'Sending…' : 'Send Email'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
